import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface GameState {
  width: number;
  height: number;
  grid: number[][];
  lights: boolean[][];
  marks: boolean[][];
  lit: boolean[][];
  conflicts: [number, number][];
  numberErrors: [number, number, number, number][];
  solved: boolean;
}

interface HistorialItem {
  tipo: 'light' | 'mark';
  x: number;
  y: number;
  anteriorLight: boolean;
  anteriorMark: boolean;
  nuevoLight: boolean;
  nuevoMark: boolean;
}

export default function LightUpGame() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dificultad_id = parseInt(searchParams.get('dificultad') || '1');
  const nivel_actual = parseInt(searchParams.get('nivel') || '1');
  
  // Generar seed - memorizado para evitar re-renders infinitos
  const urlSeedParam = searchParams.get('seed');
  const seed = useMemo(() => {
    return urlSeedParam ? parseInt(urlSeedParam) : Math.floor(Math.random() * 9000 + 1000);
  }, [urlSeedParam]);

  const [gameState, setGameState] = useState<GameState | null>(null);
  const [solution, setSolution] = useState<boolean[][] | null>(null);
  const [hintCell, setHintCell] = useState<{x: number, y: number} | null>(null);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [moveCount, setMoveCount] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [puzzleSolved, setPuzzleSolved] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [historial, setHistorial] = useState<HistorialItem[]>([]);
  const [historialIndex, setHistorialIndex] = useState(-1);

  // Mapeo de tamaños según dificultad
  const tamanioMap: { [key: number]: number } = {
    1: 7,  // Neurona
    2: 10, // Sinapsis
    3: 14, // Cerebro
    4: 14, // Genio
    5: 14  // Einstein
  };

  // Mapeo de dificultad algorítmica
  const difficultyMap: { [key: number]: string } = {
    1: 'easy',
    2: 'easy',
    3: 'medium',
    4: 'medium',
    5: 'hard'
  };

  const tamanio = tamanioMap[dificultad_id] || 7;
  const algorithm_difficulty = difficultyMap[dificultad_id] || 'medium';

  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 20);
      setBoardH(clientHeight - 20);
    }
  };

  useEffect(() => {
    if (gameState) requestAnimationFrame(measureBoard);
  }, [gameState]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);

  const cols = gameState?.width || tamanio;
  const rows = gameState?.height || tamanio;
  const gridGapH = (cols - 1) * 2 + 4;
  const gridGapV = (rows - 1) * 2 + 4;
  const cellW = boardW > 0 ? Math.floor((boardW - gridGapH) / cols) : 40;
  const cellH = boardH > 0 ? Math.floor((boardH - gridGapV) / rows) : 40;
  const fontSize = `${Math.floor(Math.min(cellW, cellH) * 0.5)}px`;

  // Dificultades (colores estandarizados)
  const dificultades = [
    { id: 1, nombre: 'Neurona', color_hex: '#22c55e', icono: '🧠', orden: 1 },
    { id: 2, nombre: 'Sinapsis', color_hex: '#3b82f6', icono: '⚡', orden: 2 },
    { id: 3, nombre: 'Cerebro', color_hex: '#a855f7', icono: '🧩', orden: 3 },
    { id: 4, nombre: 'Genio', color_hex: '#ef4444', icono: '💡', orden: 4 },
    { id: 5, nombre: 'Einstein', color_hex: '#f97316', icono: '🎓', orden: 5 },
  ];

  const dificultad_actual = dificultades.find(d => d.orden === dificultad_id) || dificultades[0];

  const { saveProgress: saveGameProgress } = useGameProgress('bombillas', dificultad_id);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Cargar el puzzle
  useEffect(() => {
    loadPuzzle();
  }, [seed, dificultad_id]);

  // Timer
  useEffect(() => {
    if (puzzleSolved) return;

    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [startTime, puzzleSolved]);

  async function loadPuzzle() {
    try {
      const response = await axios.get(`/api/games/lightup/generate`, {
        params: {
          size: tamanio,
          difficulty: algorithm_difficulty,
          seed: seed
        }
      });

      if (response.data.ok) {
        const data = response.data.data;
        setGameState({
          width: data.width,
          height: data.height,
          grid: data.grid,
          lights: Array(data.height).fill(null).map(() => Array(data.width).fill(false)),
          marks: Array(data.height).fill(null).map(() => Array(data.width).fill(false)),
          lit: Array(data.height).fill(null).map(() => Array(data.width).fill(false)),
          conflicts: [],
          numberErrors: [],
          solved: false
        });
        setSolution(data.solution ?? null);
        setHintCell(null);
        setStartTime(Date.now());
        setMoveCount(0);
        setPuzzleSolved(false);
        setHistorial([]);

        // Escribir seed en URL si no estaba ya
        if (!urlSeedParam || parseInt(urlSeedParam) !== seed) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('seed', seed.toString());
          setSearchParams(newParams, { replace: true });
        }
        setHistorialIndex(-1);
      }
    } catch (error) {
      console.error('Error cargando puzzle:', error);
      toast.error('Error al cargar el puzzle');
    }
  }

  // Calcular iluminación
  function calculateLighting(state: GameState): GameState {
    // Reset
    const newLit = state.lit.map(row => row.map(() => false));

    // Iluminar desde cada bombilla
    for (let y = 0; y < state.height; y++) {
      for (let x = 0; x < state.width; x++) {
        if (state.lights[y][x]) {
          newLit[y][x] = true;
          illuminateDirection(newLit, state.grid, state.width, state.height, x, y, 1, 0);
          illuminateDirection(newLit, state.grid, state.width, state.height, x, y, -1, 0);
          illuminateDirection(newLit, state.grid, state.width, state.height, x, y, 0, 1);
          illuminateDirection(newLit, state.grid, state.width, state.height, x, y, 0, -1);
        }
      }
    }

    return { ...state, lit: newLit };
  }

  function illuminateDirection(
    lit: boolean[][],
    grid: number[][],
    width: number,
    height: number,
    x: number,
    y: number,
    dx: number,
    dy: number
  ) {
    let nx = x + dx;
    let ny = y + dy;

    while (nx >= 0 && nx < width && ny >= 0 && ny < height) {
      if (grid[ny][nx] >= 0 || grid[ny][nx] === -2) break;
      lit[ny][nx] = true;
      nx += dx;
      ny += dy;
    }
  }

  // Verificar si está resuelto
  function checkIfSolved(state: GameState): boolean {
    // Verificar que todas las casillas estén iluminadas
    for (let y = 0; y < state.height; y++) {
      for (let x = 0; x < state.width; x++) {
        const gridValue = state.grid[y][x];
        if (gridValue === -1 && !state.lit[y][x]) {
          return false;
        }
      }
    }

    // Verificar conflictos (bombillas que se iluminan)
    for (let y = 0; y < state.height; y++) {
      for (let x = 0; x < state.width; x++) {
        if (state.lights[y][x]) {
          if (hasLightInDirection(state, x, y, 1, 0) ||
              hasLightInDirection(state, x, y, -1, 0) ||
              hasLightInDirection(state, x, y, 0, 1) ||
              hasLightInDirection(state, x, y, 0, -1)) {
            return false;
          }
        }
      }
    }

    // Verificar números en paredes
    for (let y = 0; y < state.height; y++) {
      for (let x = 0; x < state.width; x++) {
        const gridValue = state.grid[y][x];
        // Si es una pared con número (0-4)
        if (gridValue >= 0 && gridValue <= 4) {
          let count = 0;
          // Contar bombillas adyacentes (arriba, abajo, izq, der)
          const directions: [number, number][] = [[0, -1], [0, 1], [-1, 0], [1, 0]];
          for (const [dx, dy] of directions) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < state.width && ny >= 0 && ny < state.height) {
              if (state.lights[ny][nx]) {
                count++;
              }
            }
          }
          // El número debe coincidir exactamente
          if (count !== gridValue) {
            return false;
          }
        }
      }
    }

    return true;
  }

  // ─── Modo asistido: detectar errores por reglas ───────────────────────────
  // Devuelve Set de "x,y" con error activo
  function calcularErrores(state: GameState): Set<string> {
    const errores = new Set<string>();

    for (let y = 0; y < state.height; y++) {
      for (let x = 0; x < state.width; x++) {

        // 1. Bombilla que ve otra bombilla → conflicto en ambas
        if (state.lights[y][x]) {
          const dirs: [number,number][] = [[1,0],[-1,0],[0,1],[0,-1]];
          for (const [dx,dy] of dirs) {
            let nx = x+dx, ny = y+dy;
            while (nx>=0 && nx<state.width && ny>=0 && ny<state.height) {
              const g = state.grid[ny][nx];
              if (g >= 0 || g === -2) break;       // pared → stop
              if (state.lights[ny][nx]) {
                errores.add(`${x},${y}`);
                errores.add(`${nx},${ny}`);
              }
              nx+=dx; ny+=dy;
            }
          }
        }

        // 2. Celda numerada con demasiadas bombillas adyacentes
        // 3. Celda numerada con imposible llegar al clue (espacios libres < restantes)
        const clue = state.grid[y][x];
        if (clue >= 0) {
          const adjDirs: [number,number][] = [[1,0],[-1,0],[0,1],[0,-1]];
          let luces = 0, libres = 0;
          for (const [dx,dy] of adjDirs) {
            const nx=x+dx, ny=y+dy;
            if (nx<0||nx>=state.width||ny<0||ny>=state.height) continue;
            const g = state.grid[ny][nx];
            if (g !== -1) continue;              // solo celdas blancas
            if (state.lights[ny][nx]) luces++;
            else if (!state.marks[ny][nx]) libres++;
          }
          // Demasiadas bombillas
          if (luces > clue) errores.add(`${x},${y}`);
          // Imposible completar: luces + libres < clue
          if (luces + libres < clue) errores.add(`${x},${y}`);
        }
      }
    }
    return errores;
  }

  function hasLightInDirection(state: GameState, x: number, y: number, dx: number, dy: number): boolean {
    let nx = x + dx;
    let ny = y + dy;

    while (nx >= 0 && nx < state.width && ny >= 0 && ny < state.height) {
      if (state.grid[ny][nx] >= 0 || state.grid[ny][nx] === -2) break;
      if (state.lights[ny][nx]) return true;
      nx += dx;
      ny += dy;
    }
    return false;
  }

  // Guardar progreso
  async function saveProgress(tiempo: number, movimientos: number) {
    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: seed,
        tiempo: tiempo,
        movimientos: movimientos
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos,
        completado: true,
        datos_juego: {
          tablero_id: seed,
          size: gameState?.width || 0,
        },
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  }

  // Manejar victoria
  async function handleVictory() {
    if (puzzleSolved) return;
    setPuzzleSolved(true);

    // Guardar progreso
    await saveProgress(elapsedTime, moveCount);
  }

  // Cicla el estado de la celda: vacío → 💡 → ✕ → vacío
  function cycleCell(x: number, y: number) {
    if (!gameState || puzzleSolved) return;

    const newState = { ...gameState };
    newState.lights = gameState.lights.map(row => [...row]);
    newState.marks = gameState.marks.map(row => [...row]);

    const anteriorLight = newState.lights[y][x];
    const anteriorMark = newState.marks[y][x];

    if (!anteriorLight && !anteriorMark) {
      // vacío → bombilla
      newState.lights[y][x] = true;
      newState.marks[y][x] = false;
    } else if (anteriorLight) {
      // bombilla → marca
      newState.lights[y][x] = false;
      newState.marks[y][x] = true;
    } else {
      // marca → vacío
      newState.lights[y][x] = false;
      newState.marks[y][x] = false;
    }

    const newHistorial = historial.slice(0, historialIndex + 1);
    newHistorial.push({
      tipo: 'light',
      x,
      y,
      anteriorLight,
      anteriorMark,
      nuevoLight: newState.lights[y][x],
      nuevoMark: newState.marks[y][x],
    });
    setHistorial(newHistorial);
    setHistorialIndex(newHistorial.length - 1);
    setMoveCount(moveCount + 1);

    const updatedState = calculateLighting(newState);
    setGameState(updatedState);

    if (checkIfSolved(updatedState)) {
      handleVictory();
    }
  }

  // Deshacer
  function deshacer() {
    if (historialIndex < 0 || puzzleSolved || !gameState) return;

    const movimiento = historial[historialIndex];
    const newState = { ...gameState };
    newState.lights = gameState.lights.map(row => [...row]);
    newState.marks = gameState.marks.map(row => [...row]);

    // Restaurar estado anterior
    newState.lights[movimiento.y][movimiento.x] = movimiento.anteriorLight;
    newState.marks[movimiento.y][movimiento.x] = movimiento.anteriorMark;

    setHistorialIndex(historialIndex - 1);

    const updatedState = calculateLighting(newState);
    setGameState(updatedState);
  }

  // Rehacer
  function rehacer() {
    if (historialIndex >= historial.length - 1 || puzzleSolved || !gameState) return;

    const movimiento = historial[historialIndex + 1];
    const newState = { ...gameState };
    newState.lights = gameState.lights.map(row => [...row]);
    newState.marks = gameState.marks.map(row => [...row]);

    // Aplicar estado nuevo
    newState.lights[movimiento.y][movimiento.x] = movimiento.nuevoLight;
    newState.marks[movimiento.y][movimiento.x] = movimiento.nuevoMark;

    setHistorialIndex(historialIndex + 1);

    const updatedState = calculateLighting(newState);
    setGameState(updatedState);
  }

  // ─── Pista ────────────────────────────────────────────────────────────────

  function darPista() {
    if (!gameState || !solution || puzzleSolved) return;

    const newState = { ...gameState };
    newState.lights = gameState.lights.map(row => [...row]);
    newState.marks  = gameState.marks.map(row => [...row]);

    // 1. Primero corregir una bombilla mal puesta
    for (let y = 0; y < gameState.height; y++) {
      for (let x = 0; x < gameState.width; x++) {
        if (newState.lights[y][x] && !solution[y][x]) {
          newState.lights[y][x] = false;
          const updated = calculateLighting(newState);
          setGameState(updated);
          setHintCell({ x, y });
          setTimeout(() => setHintCell(null), 1600);
          toast.info('Pista: bombilla incorrecta eliminada');
          setMoveCount(c => c + 1);
          return;
        }
      }
    }

    // 2. Colocar una bombilla que falta
    for (let y = 0; y < gameState.height; y++) {
      for (let x = 0; x < gameState.width; x++) {
        if (solution[y][x] && !newState.lights[y][x]) {
          newState.lights[y][x] = true;
          newState.marks[y][x]  = false;
          const updated = calculateLighting(newState);
          setGameState(updated);
          setHintCell({ x, y });
          setTimeout(() => setHintCell(null), 1600);
          toast.info('Pista: bombilla colocada');
          setMoveCount(c => c + 1);
          if (checkIfSolved(updated)) handleVictory();
          return;
        }
      }
    }

    toast.success('¡Todo correcto!');
  }

  // ─── Ver solución ─────────────────────────────────────────────────────────

  function verSolucion() {
    if (!gameState || !solution) return;
    if (!window.confirm('¿Mostrar la solución? El progreso no se guardará.')) return;

    const newState = { ...gameState };
    newState.lights = solution.map(row => [...row]);
    newState.marks  = Array(gameState.height).fill(null).map(() => Array(gameState.width).fill(false));
    const updated = calculateLighting(newState);
    setGameState(updated);
  }

  // ─── Reiniciar ────────────────────────────────────────────────────────────

  function resetGame() {
    if (!gameState) return;

    const newState = { ...gameState };
    newState.lights = Array(gameState.height).fill(null).map(() => Array(gameState.width).fill(false));
    newState.marks = Array(gameState.height).fill(null).map(() => Array(gameState.width).fill(false));

    setMoveCount(0);
    setStartTime(Date.now());
    setPuzzleSolved(false);
    setHistorial([]);
    setHistorialIndex(-1);
    setHintCell(null);

    const updatedState = calculateLighting(newState);
    setGameState(updatedState);
  }

  // Nuevo juego
  function newGame() {
    const newSeed = Math.floor(Math.random() * 9000 + 1000);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('seed', newSeed.toString());
    setSearchParams(newParams);
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  if (!gameState) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#667eea] to-[#764ba2]">
        <div className="text-white text-2xl">Cargando puzzle...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden bg-gradient-to-br from-[#667eea] to-[#764ba2] flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🌓 Luces y Sombras</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                const newParams = new URLSearchParams();
                newParams.set('dificultad', d.orden.toString());
                setSearchParams(newParams);
              }}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                dificultad_id === d.orden
                  ? 'bg-white text-purple-700'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Undo */}
          <button onClick={deshacer} disabled={historialIndex < 0} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Redo */}
          <button onClick={rehacer} disabled={historialIndex >= historial.length - 1} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)} title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </button>
          {/* Pista */}
          <button onClick={darPista} disabled={puzzleSolved || !solution} title="Pista"
            className="p-1.5 rounded bg-orange-500/70 hover:bg-orange-500 text-white transition-all disabled:opacity-40">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>
            </svg>
          </button>
          {/* Ver solución */}
          <button onClick={verSolucion} disabled={puzzleSolved || !solution} title="Ver solución"
            className="p-1.5 rounded bg-red-500/70 hover:bg-red-500 text-white transition-all disabled:opacity-40">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          {/* Reset */}
          <button onClick={resetGame} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* New board */}
          <button onClick={newGame} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* How to play */}
          <button onClick={() => setShowHowToPlay(true)} title="Como se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-black/10 px-3 py-1 flex items-center gap-4 text-white/80 text-xs">
        <span>{dificultad_actual.icono} {dificultad_actual.nombre} — {tamanio}×{tamanio}</span>
        <span>⏱ {formatTime(elapsedTime)}</span>
        <span>🎯 {moveCount} mov</span>
        <span className="text-white/50">Toca para ciclar: vacío → 💡 → ✕ → vacío</span>
      </div>

      {/* Board Area */}
      <div ref={boardAreaRef} className="flex-1 flex items-center justify-center p-2 min-h-0 overflow-hidden">
        <div
          className="inline-grid gap-0.5 bg-gray-800 p-0.5 rounded-lg"
          style={{
            gridTemplateColumns: `repeat(${gameState.width}, ${cellW}px)`,
            gridTemplateRows: `repeat(${gameState.height}, ${cellH}px)`,
          }}
        >
          {(() => {
            const errores = modoAsistido ? calcularErrores(gameState) : new Set<string>();
            return gameState.grid.map((row, y) =>
            row.map((cell, x) => {
              const hasLight = gameState.lights[y][x];
              const hasMark = gameState.marks[y][x];
              const isLit = gameState.lit[y][x];
              const isWall = cell >= 0 || cell === -2;
              const isHinted = hintCell?.x === x && hintCell?.y === y;
              const hasError = modoAsistido && errores.has(`${x},${y}`);

              let cellClass = 'flex items-center justify-center font-bold transition-all select-none';
              let bgColor = '';

              let bgColorHex: string;
              let cellBorder: string | undefined;
              let cursorStyle = 'pointer';
              let cellTextColor: string | undefined;

              if (isWall) {
                cursorStyle = 'default';
                if (hasError) {
                  bgColorHex = '#7f1d1d'; cellBorder = '2px solid #ef4444'; cellTextColor = '#ffffff';
                } else if (cell >= 0) {
                  // Pared numerada: pista del sistema — gris
                  bgColorHex = '#9ca3af'; cellBorder = '3px solid #6b7280'; cellTextColor = '#1f2937';
                } else {
                  // Pared sin número: oscura
                  bgColorHex = '#1e293b'; cellTextColor = '#ffffff';
                }
              } else if (isHinted) {
                bgColorHex = '#fb923c'; cellBorder = '2px solid #ea580c';
              } else if (hasLight) {
                bgColorHex = hasError ? '#f87171' : '#fff176';
                if (hasError) cellBorder = '2px solid #ef4444';
              } else if (hasMark) {
                bgColorHex = isLit ? '#fff9c4' : '#f3f4f6'; cellTextColor = isLit ? '#6b7280' : '#4b5563';
              } else if (isLit) {
                bgColorHex = '#fff9c4';
              } else {
                bgColorHex = '#f3f4f6';
              }

              return (
                <div
                  key={`${x}-${y}`}
                  className={`${cellClass}`}
                  style={{
                    width: `${cellW}px`,
                    height: `${cellH}px`,
                    fontSize,
                    backgroundColor: bgColorHex,
                    border: cellBorder,
                    color: cellTextColor,
                    cursor: cursorStyle,
                  }}
                  onClick={() => !isWall && cycleCell(x, y)}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {isWall && cell >= 0 ? cell : hasLight ? '💡' : hasMark ? '✕' : ''}
                </div>
              );
            }));
          })()}
        </div>
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Luces y Sombras"
        gameIcon="🌓"
        objective="Ilumina todas las casillas del tablero colocando bombillas estratégicamente."
        rules={[
          'Todas las casillas blancas deben estar iluminadas',
          'Las bombillas iluminan toda su fila y columna hasta encontrar una pared negra',
          'Las bombillas NO pueden iluminarse entre sí',
          'Los números en las paredes negras indican cuántas bombillas deben estar adyacentes (arriba, abajo, izquierda, derecha) a esa pared',
          'Las paredes sin número pueden tener cualquier cantidad de bombillas adyacentes',
        ]}
        howToPlay="Toca/click para ciclar el estado: vacío → bombilla 💡 → marca ✕ → vacío."
        author="Simon Tatham"
      />

      <VictoryModal
        isOpen={puzzleSolved}
        onClose={() => setPuzzleSolved(false)}
        stats={{ tiempo: elapsedTime, movimientos: moveCount }}
      />
    </div>
  );
}
